import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { find } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, ShowProps, useDataProvider } from "react-admin";
import { GridinRowsWithSpace } from "utils/style";
import IconPlus from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { GetParam, StudentResources } from "utils/tools";
import { ccaDropOffLocationType } from "../const";
import CcaScheduleDetailColumn from "./CcaScheduleDetailColumn";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
    padding: "1rem",
  },
  summaryButton: {
    position: "relative",
    top: 20,
    padding: "8px 16px",
  },
  academicYearLabel: {
    marginTop: "5px",
  },
  MuiAccordionSummary: {
    "& .MuiAccordionSummary-content": {
      margin: "15px 0px 5px",
    },
  },
});

const CcaStudentScheduleTable = (props: ShowProps) => {
  const classes = useStyles();
  const provider = useDataProvider();
  const param = GetParam();

  const [ccaAcademicYear, setCcaAcademicYear] = useState<
    ICcaAcademicYear[] | null
  >(null);
  const [ccaAcademicYearId, setCcaAcademicYearId] = useState<number>();
  const [ccaAcademicTerms, setCcaAcademicTerms] = useState<ICcaAcademicTerm[]>(
    []
  );
  const [ccaAreas, setCcaAreas] = useState<IArea[]>([]);
  const [ccaStudentSchedules, setCcaStudentSchedules] = useState<
    ICcaStudentSchedule[]
  >([]);
  const [fixedLocations, setFixedLocations] = useState<
    ICcaFixedLocationDropOff[]
  >([]);
  const [ccaWaves, setCcaWaves] = useState<IBusSchedule[]>([]);
  const [loadingCca, setLoadingCca] = useState<boolean>(false);

  const format = "DD / MM / YYYY";

  const { id } = props;
  const studentResources = StudentResources(Number(id));

  useEffect(() => {
    provider
      .getList("ccaAcademicYears", {
        filter: {
          SchoolId: Number(studentResources.schoolId),
        },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setCcaAcademicYear(data as ICcaAcademicYear[]);

        // The default is active CcaAcademicYearId
        // If there is not active CcaAcademicYearId,
        // I assume the default is latest CcaAcademicYearId
        const activeCcaAcademicYear = data.find(
          (item) => item.status.toLowerCase() === "active"
        );

        if (activeCcaAcademicYear) {
          setCcaAcademicYearId(activeCcaAcademicYear.id as number);
        } else {
          if (data.length > 0) {
            setCcaAcademicYearId(data[data.length - 1].id as number);
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ccaAcademicYear) {
      provider
        .getList("ccaAcademicTerms", {
          filter: { CcaAcademicYearId: Number(ccaAcademicYearId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaAcademicTerms(data as ICcaAcademicTerm[]);
        });

      setLoadingCca(true);
      provider
        .getList("ccaStudentSchedules", {
          filter: {
            StudentId: Number(id),
            CcaAcademicYearId: Number(ccaAcademicYearId),
          },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "StartedAt",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaStudentSchedules(data as ICcaStudentSchedule[]);
          setLoadingCca(false);
        });

      provider
        .getList("ccaAreas", {
          filter: { SchoolId: Number(studentResources.schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaAreas(data as IArea[]);
        });

      provider
        .getList("ccaFixedLocationDropOffs", {
          filter: { SchoolId: Number(studentResources.schoolId) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setFixedLocations(data as ICcaFixedLocationDropOff[]);
        });

      provider
        .getList("busSchedules", {
          filter: { SchoolId: Number(studentResources.schoolId), Type: "cca" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaWaves(data as IBusSchedule[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccaAcademicYearId]);

  const handleCcaAcademicYearChange = (event: any) => {
    setCcaAcademicYearId(event.target.value);
  };

  const ccaScheduleDropOffTypeFormatter = (id: string, source: any): string => {
    const item = source.find((type: any) => type.id === id);
    return item ? item.name : "-";
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="cca-student-schedule-content"
        id="cca-student-schedule-header"
        className={classes.MuiAccordionSummary}
      >
        <h3 className={classes.academicYearLabel}>CCA Student Schedules</h3>
      </AccordionSummary>
      <AccordionDetails>
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12} style={GridinRowsWithSpace}>
            <div>
              <FormControl variant="filled" className="form-control">
                <InputLabel id="ccaAcademicYear">
                  CCA Academic Year
                </InputLabel>
                {ccaAcademicYearId && (
                  <Select
                    labelId="ccaAcademicYear"
                    id="ccaAcademicYear"
                    value={ccaAcademicYearId}
                    onChange={handleCcaAcademicYearChange}
                  >
                    {ccaAcademicYear.map((value: any) => {
                      return (
                        <MenuItem value={value.id} key={value.id}>
                          {value.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </div>
            <div>
              <Button
                onClick={() =>
                  props.history.push(
                    `/ccaStudentSchedules/create?source=${JSON.stringify({
                      studentId: Number(id),
                      schoolId: Number(studentResources.schoolId),
                      parentId: Number(studentResources.parent?.id),
                    })}&studentId=${Number(id)}${
                      param.get("parentId")
                        ? `&parentId=${param.get("parentId")}`
                        : ""
                    }`
                  )
                }
                label="Update CCA Schedule"
              >
                <IconPlus />
              </Button>
            </div>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Term</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Drop-off Type</TableCell>
                    <TableCell>Schedule Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ccaStudentSchedules.length > 0 ? (
                    <>
                      {ccaStudentSchedules.map(
                        (
                          ccaStudentSchedule: ICcaStudentSchedule,
                          key: number
                        ) => {
                          return (
                            <TableRow key={`cca-schedule-${key}`}>
                              <TableCell>
                                {(() => {
                                  const term = find(ccaAcademicTerms, [
                                    "id",
                                    ccaStudentSchedule.termId,
                                  ]);

                                  if (term === undefined) {
                                    return;
                                  }

                                  return term.name;
                                })()}
                              </TableCell>
                              <TableCell>
                                {`${moment
                                  .utc(ccaStudentSchedule.startDate)
                                  .format(format)} - ${moment
                                  .utc(ccaStudentSchedule.endDate)
                                  .format(format)}`}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  if (
                                    ccaStudentSchedule.detail === null ||
                                    ccaStudentSchedule.isTaking === false
                                  ) {
                                    return "---";
                                  }

                                  return ccaScheduleDropOffTypeFormatter(
                                    ccaStudentSchedule.dropOffType,
                                    ccaDropOffLocationType
                                  );
                                })()}
                              </TableCell>
                              {ccaStudentSchedule.detail === null ||
                              ccaStudentSchedule.isTaking === false ? (
                                <TableCell>---</TableCell>
                              ) : (
                                <TableCell>
                                  <CcaScheduleDetailColumn
                                    schedule={ccaStudentSchedule.detail}
                                    dropOffType={
                                      ccaStudentSchedule.dropOffType
                                    }
                                    ccaAreas={ccaAreas}
                                    fixedLocations={fixedLocations}
                                    waves={ccaWaves}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                      )}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        {loadingCca ? "Loading..." : "No results found."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </SanitizedGrid>
      </AccordionDetails>
    </Accordion>
  );
};

export default CcaStudentScheduleTable;
